import { useMemo, useState } from 'react';

export type SortDirection = 'asc' | 'desc';

/**
 * Generic click-to-sort helper for grid/table components.
 * Comparators may be supplied per-column for non-trivial fields (dates, nested values, computed scores).
 */
export function useTableSort<T>(
  data: T[],
  defaultColumn: string,
  defaultDirection: SortDirection = 'asc',
  comparators?: Partial<Record<string, (a: T, b: T) => number>>
) {
  const [sortColumn, setSortColumn] = useState<string>(defaultColumn);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultDirection);

  const sortedData = useMemo(() => {
    const arr = [...data];
    arr.sort((a, b) => {
      let result = 0;
      const customFn = comparators?.[sortColumn];
      if (customFn) {
        result = customFn(a, b);
      } else {
        const valA = (a as any)[sortColumn];
        const valB = (b as any)[sortColumn];
        if (valA == null && valB == null) result = 0;
        else if (valA == null) result = -1;
        else if (valB == null) result = 1;
        else if (typeof valA === 'number' && typeof valB === 'number') result = valA - valB;
        else result = String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
      }
      return sortDirection === 'asc' ? result : -result;
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, sortColumn, sortDirection]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortIcon = (column: string): string => {
    if (sortColumn !== column) return '\u21c5';
    return sortDirection === 'asc' ? '\u25b2' : '\u25bc';
  };

  const sortIconClass = (column: string) => (sortColumn === column ? 'sort-icon active' : 'sort-icon');

  return { sortedData, sortColumn, sortDirection, handleSort, sortIcon, sortIconClass };
}
